import { ClassicEditor } from '@ckeditor/ckeditor5-editor-classic';
import { Alignment } from '@ckeditor/ckeditor5-alignment';
import { Bold, Italic, Underline, Strikethrough, Superscript, Subscript } from '@ckeditor/ckeditor5-basic-styles';
import { Essentials } from '@ckeditor/ckeditor5-essentials';
import { Heading } from '@ckeditor/ckeditor5-heading';
import { Clipboard } from "@ckeditor/ckeditor5-clipboard";
import TwoColumnsBox from './plugins/two-columns/src/two-columns';
import { List } from '@ckeditor/ckeditor5-list';
import { Paragraph } from '@ckeditor/ckeditor5-paragraph';
import { FontSize, FontColor } from '@ckeditor/ckeditor5-font';
import { BlockQuote } from '@ckeditor/ckeditor5-block-quote';
import { HorizontalLine } from '@ckeditor/ckeditor5-horizontal-line';
import { Indent } from '@ckeditor/ckeditor5-indent';
import { Link, LinkImage } from "./plugins/link";
import { MediaEmbed } from '@ckeditor/ckeditor5-media-embed';
import { RemoveFormat } from '@ckeditor/ckeditor5-remove-format';
import { Table, TableCellProperties, TableProperties, TableToolbar } from '@ckeditor/ckeditor5-table';
import { TextTransformation } from '@ckeditor/ckeditor5-typing';
import IframeEmbed from './plugins/iframe/src/iframeembed';
import Shortcodes from "./plugins/shortcodes/src/shortcodes";
import ContentTemplates from './plugins/content-templates/content-templates.js';
import ThreeColumnsBox from './plugins/three-columns/src/three-columns.js';
import FourColumnsBox from './plugins/four-columns/src/four-columns.js';
import { SimpleUploadAdapter } from '@ckeditor/ckeditor5-upload';
import Image from '@ckeditor/ckeditor5-image/src/image.js';
import ImageCaption from '@ckeditor/ckeditor5-image/src/imagecaption.js';
import ImageResize from '@ckeditor/ckeditor5-image/src/imageresize.js';
import ImageStyle from '@ckeditor/ckeditor5-image/src/imagestyle.js';
import ImageToolbar from '@ckeditor/ckeditor5-image/src/imagetoolbar.js';
import ImageUpload from '@ckeditor/ckeditor5-image/src/imageupload.js';

window.editorSettings = window.editorSettings || {
    h2Support: false,
    fontColors: [],
    tableColors: ['#00775E', '#213b18', '#7f907d'],
    shortcodes: [],
    contentTemplates: [],
    uploadEndpoint: '/media/images/upload',

    includePlugins: [],
    excludePlugins: [],
    includeToolbar: [],
    excludeToolbar: [],
};

function initEditor(field, callback) {
    if (typeof field._editor !== 'undefined') {
        return;
    }

    ClassicEditor
        .create(field, getConfiguration(field))
        .then(editor => {
            field._editor = editor;
            const editingView = editor.editing.view;

            editingView.document.on('clipboardInput', (event, data) => {
                let contentHtml = data.dataTransfer.getData('text/html');
                let contentText = data.dataTransfer.getData('text/plain');
                let content = contentHtml || contentText;

                if (!content) {
                    return;
                }
                if (editor.isReadOnly || data.method !== 'paste') {
                    return;
                }

                content = content
                    .replace('<meta charset="UTF-8">', '')
                    .replace('<meta charset="utf-8">', '');

                let node = document.createElement('div');
                node.innerHTML = content;

                let nodeArr = node.querySelectorAll('*');
                let allowed = {
                    'IMG': ['src', 'width', 'height'],
                    'IFRAME': ['src', 'height'],
                    'A': ['href', 'target', 'rel'],
                };

                nodeArr.forEach(node => {
                    [...node.attributes].forEach(attr => {
                        if (allowed[node.tagName] && allowed[node.tagName].includes(attr.name)) {
                            return;
                        }

                        node.removeAttribute(attr.name);
                    });
                });

                data.content = editor.data.htmlProcessor.toView(node.innerHTML);
                editingView.scrollToTheSelection();
            }, {
                priority: 'high'
            });

            if (callback) {
                callback(editor);
            }
        })
        .catch(error => console.error(error));

    function getEditorType(field) {
        return field.dataset.editorType === 'simple' ? 'simple' : 'full';
    }

    function parseCsvList(val) {
        if (!val) {
            return [];
        }

        return String(val)
            .split(',')
            .map(s => s.trim())
            .filter(Boolean);
    }

    function unique(arr) {
        return [...new Set(arr)];
    }

    function mergeLists(baseArr, includeArr, excludeArr) {
        let out = [...(baseArr || [])];

        (includeArr || []).forEach(x => out.push(x));

        out = unique(out);

        if (excludeArr && excludeArr.length) {
            out = out.filter(x => !excludeArr.includes(x));
        }

        return out;
    }

    function getPerFieldOverrides(field) {
        return {
            includePlugins: parseCsvList(field.dataset.includePlugins),
            excludePlugins: parseCsvList(field.dataset.excludePlugins),
            includeToolbar: parseCsvList(field.dataset.includeToolbar),
            excludeToolbar: parseCsvList(field.dataset.excludeToolbar),
        };
    }

    function getConfiguration(field) {
        let imagesSupport = field.classList.contains('editor--images');
        let editorType = getEditorType(field);

        const overrides = getPerFieldOverrides(field);

        let configuration = {
            language: document.documentElement.lang,
            plugins: getConfigurationPlugins(imagesSupport, editorType, overrides),
            toolbar: {
                items: getConfigurationToolbarItems(imagesSupport, editorType, overrides),
                shouldNotGroupWhenFull: true,
            },
            table: {
                contentToolbar: ['tableColumn', 'tableRow', 'mergeTableCells', 'tableCellProperties', 'tableProperties'],
                tableProperties: {
                    borderColors: getConfigurationTableColors(),
                    backgroundColors: getConfigurationTableColors()
                },
                tableCellProperties: {
                    borderColors: getConfigurationTableColors(),
                    backgroundColors: getConfigurationTableColors()
                }
            },
            mediaEmbed: {
                previewsInData: true
            },
            link: {
                decorators: {
                    openInNewTab: {
                        mode: 'manual',
                        label: 'Open in a new tab',
                        attributes: { target: '_blank', rel: 'noopener' }
                    },
                    openInNewTabNofollow: {
                        mode: 'manual',
                        label: translate('Open in a new tab + NOFOLLOW'),
                        attributes: { target: '_blank', rel: 'noopener nofollow' }
                    },
                    nofollow: {
                        mode: 'manual',
                        label: translate('NOFOLLOW'),
                        attributes: { rel: 'nofollow' }
                    }
                }
            },
            heading: getConfigurationHeadings(field, editorType),
            fontColor: getConfigurationFontColors(),
        };

        if (imagesSupport && editorType !== 'simple') {
            configuration['image'] = {
                resizeUnit: "%",
                resizeOptions: [
                    { name: 'resizeImage:original', value: null, icon: 'original' },
                    { name: 'resizeImage:10', value: '10', icon: 'small' },
                    { name: 'resizeImage:25', value: '25', icon: 'small' },
                    { name: 'resizeImage:50', value: '50', icon: 'medium' },
                    { name: 'resizeImage:75', value: '75', icon: 'large' },
                    { name: 'resizeImage:100', value: '100', icon: 'large' },
                ],
                toolbar: [
                    'imageTextAlternative',
                    'toggleImageCaption',
                    'linkImage',
                    '|',
                    'imageStyle:inline',
                    'imageStyle:alignLeft',
                    'imageStyle:alignCenter',
                    'imageStyle:alignRight',
                    '|',
                    'resizeImage',
                ]
            };

            configuration['simpleUpload'] = {
                uploadUrl: window.editorSettings.uploadEndpoint,
            };
        }

        return configuration;
    }

    function getConfigurationPlugins(imagesSupport, editorType, overrides) {
        let pluginsFull = [
            Alignment,
            BlockQuote,
            Bold,
            Italic,
            Underline,
            Strikethrough,
            Superscript,
            Essentials,
            Subscript,
            Clipboard,
            TwoColumnsBox,
            FontColor,
            FontSize,
            Heading,
            HorizontalLine,
            Indent,
            Link,
            List,
            MediaEmbed,
            Paragraph,
            RemoveFormat,
            Table,
            TableCellProperties,
            TableProperties,
            TableToolbar,
            TextTransformation,
            IframeEmbed,
            ThreeColumnsBox,
            FourColumnsBox,
            SimpleUploadAdapter,
        ];

        let pluginsSimple = [
            Essentials,
            Paragraph,
            Clipboard,
            Heading,
            Bold,
            Italic,
            Underline,
            List,
            RemoveFormat,
            TextTransformation,
        ];

        let plugins = editorType === 'simple' ? pluginsSimple : pluginsFull;

        if (window.editorSettings.shortcodes.length) {
            plugins = plugins.concat([Shortcodes]);
        }
        if (window.editorSettings.contentTemplates.length) {
            plugins = plugins.concat([ContentTemplates]);
        }

        if (imagesSupport && editorType !== 'simple') {
            plugins = plugins.concat([
                Image,
                ImageCaption,
                ImageResize,
                ImageStyle,
                ImageToolbar,
                ImageUpload,
                LinkImage,
                SimpleUploadAdapter,
            ]);
        }

        const pluginNameMap = new Map([
            ['Alignment', Alignment],
            ['BlockQuote', BlockQuote],
            ['Bold', Bold],
            ['Italic', Italic],
            ['Underline', Underline],
            ['Strikethrough', Strikethrough],
            ['Superscript', Superscript],
            ['Subscript', Subscript],
            ['Essentials', Essentials],
            ['Clipboard', Clipboard],
            ['Paragraph', Paragraph],
            ['Heading', Heading],
            ['FontColor', FontColor],
            ['FontSize', FontSize],
            ['Indent', Indent],
            ['Link', Link],
            ['List', List],
            ['MediaEmbed', MediaEmbed],
            ['RemoveFormat', RemoveFormat],
            ['HorizontalLine', HorizontalLine],
            ['Table', Table],
            ['TableCellProperties', TableCellProperties],
            ['TableProperties', TableProperties],
            ['TableToolbar', TableToolbar],
            ['TextTransformation', TextTransformation],
            ['IframeEmbed', IframeEmbed],
            ['TwoColumnsBox', TwoColumnsBox],
            ['ThreeColumnsBox', ThreeColumnsBox],
            ['FourColumnsBox', FourColumnsBox],
            ['Shortcodes', Shortcodes],
            ['ContentTemplates', ContentTemplates],
            ['SimpleUploadAdapter', SimpleUploadAdapter],
            ['Image', Image],
            ['ImageCaption', ImageCaption],
            ['ImageResize', ImageResize],
            ['ImageStyle', ImageStyle],
            ['ImageToolbar', ImageToolbar],
            ['ImageUpload', ImageUpload],
            ['LinkImage', LinkImage],
        ]);

        const globalIncludeNames = parseCsvList(window.editorSettings.includePlugins);
        const globalExcludeNames = parseCsvList(window.editorSettings.excludePlugins);

        const gInc = globalIncludeNames.map(n => pluginNameMap.get(n)).filter(Boolean);
        const gExc = globalExcludeNames.map(n => pluginNameMap.get(n)).filter(Boolean);

        const fInc = (overrides.includePlugins || []).map(n => pluginNameMap.get(n)).filter(Boolean);
        const fExc = (overrides.excludePlugins || []).map(n => pluginNameMap.get(n)).filter(Boolean);

        let out = plugins.slice();
        out = out.concat(gInc).concat(fInc);
        out = unique(out);

        const excAll = unique(gExc.concat(fExc));
        if (excAll.length) {
            out = out.filter(p => !excAll.includes(p));
        }

        return out;
    }

    function getConfigurationToolbarItems(imagesSupport, editorType, overrides) {
        let toolbarFull = [
            'heading',
            '|',
            'alignment',
            'fontColor',
            'bold',
            'italic',
            'underline',
            'strikethrough',
            '|',
            'bulletedList',
            'numberedList',
            '|',
            'link',
            'insertTable',
            'mediaEmbed',
            'iframeEmbed',
            'blockQuote',
            'horizontalLine',
            '|',
            'twoColumnsBox',
            'threeColumnsBox',
            'fourColumnsBox',
            '|',
            'superscript',
            'subscript',
            '|',
            'removeFormat',
            'undo',
            'redo',
        ];

        let toolbarSimple = [
            'heading',
            '|',
            'bold',
            'italic',
            'underline',
            '|',
            'bulletedList',
            'numberedList',
            '|',
            'removeFormat',
            'undo',
            'redo',
        ];

        let toolbar = editorType === 'simple' ? toolbarSimple : toolbarFull;

        if (imagesSupport && editorType !== 'simple') {
            toolbar = toolbar.concat(['imageUpload']);
        }
        if (window.editorSettings.shortcodes.length) {
            toolbar = toolbar.concat(['shortcodes']);
        }
        if (window.editorSettings.contentTemplates.length) {
            toolbar = toolbar.concat(['contentTemplates']);
        }

        const globalInc = parseCsvList(window.editorSettings.includeToolbar);
        const globalExc = parseCsvList(window.editorSettings.excludeToolbar);

        toolbar = mergeLists(toolbar, globalInc, globalExc);
        toolbar = mergeLists(toolbar, overrides.includeToolbar, overrides.excludeToolbar);

        toolbar = sanitizeToolbar(toolbar);

        return toolbar;
    }

    function sanitizeToolbar(items) {
        const out = [];

        for (let i = 0; i < items.length; i++) {
            const it = items[i];

            if (it === '|') {
                if (!out.length) {
                    continue;
                }
                if (out[out.length - 1] === '|') {
                    continue;
                }
                out.push(it);
                continue;
            }

            out.push(it);
        }

        while (out.length && out[out.length - 1] === '|') {
            out.pop();
        }

        return out;
    }

    function getConfigurationHeadings(field, editorType) {
        let counter = 0;
        let headings = {
            options: [
                { model: 'paragraph', title: 'Paragraph', class: 'ck-heading_paragraph' },
            ]
        };

        if (editorType === 'simple') {
            headings.options.push({ model: 'heading2', view: 'h2', title: 'Heading 2', class: 'ck-heading_heading1' });
            headings.options.push({ model: 'heading3', view: 'h3', title: 'Heading 3', class: 'ck-heading_heading2' });
            return headings;
        }

        if (field.dataset.h1Support) {
            headings.options.push({ model: 'heading1', view: 'h1', title: 'Heading 1', class: 'ck-heading_heading' + (++counter) });
        }

        if (field.dataset.h2Support || window.editorSettings.h2Support) {
            headings.options.push({ model: 'heading2', view: 'h2', title: 'Heading 2', class: 'ck-heading_heading' + (++counter) });
        }

        headings.options.push({ model: 'heading3', view: 'h3', title: 'Heading 3', class: 'ck-heading_heading' + (++counter) });
        headings.options.push({ model: 'heading4', view: 'h4', title: 'Heading 4', class: 'ck-heading_heading' + (++counter) });

        return headings;
    }

    function getConfigurationTableColors() {
        return window.editorSettings.tableColors || [];
    }

    function getConfigurationFontColors() {
        let config = {
            colors: [
                { color: '#000000' },
                { color: '#111727' },
                { color: '#202938' },
                { color: '#384152' },
                { color: '#4c5564' },
                { color: '#6a7380' },
                { color: '#9da2ae' },
                { color: '#d2d3de', hasBorder: true },
                { color: '#e6e8ec', hasBorder: true },
                { color: '#f3f5f9', hasBorder: true },

                { color: '#7f1d1e' },
                { color: '#9b1b1d' },
                { color: '#b91b20' },
                { color: '#dc2624' },
                { color: '#f04342' },
                { color: '#f77070' },
                { color: '#fca5a7' },
                { color: '#fdcbcc', hasBorder: true },
                { color: '#ffe1e0', hasBorder: true },
                { color: '#fcf3f2', hasBorder: true },

                { color: '#793410' },
                { color: '#933f0e' },
                { color: '#933f0e' },
                { color: '#d87706' },
                { color: '#f39e0b' },
                { color: '#fcbf22' },
                { color: '#f9d44c' },
                { color: '#fbe787', hasBorder: true },
                { color: '#fff2c7', hasBorder: true },
                { color: '#fffbea', hasBorder: true },

                { color: '#074c3c' },
                { color: '#076046' },
                { color: '#057758' },
                { color: '#07966d' },
                { color: '#10b884' },
                { color: '#31d59b' },
                { color: '#6de7b5' },
                { color: '#a7f4ce', hasBorder: true },
                { color: '#d0fbe3', hasBorder: true },
                { color: '#ecfdf7', hasBorder: true },

                { color: '#1b3f87' },
                { color: '#1e40ad' },
                { color: '#1c4fd8' },
                { color: '#2463e9' },
                { color: '#3c82f5' },
                { color: '#61a6fa' },
                { color: '#93c5fd' },
                { color: '#bfdcfe', hasBorder: true },
                { color: '#dbecff', hasBorder: true },
                { color: '#f0f5ff', hasBorder: true },

                { color: '#322d81' },
                { color: '#3530a4' },
                { color: '#4539ca' },
                { color: '#5046e4' },
                { color: '#6365f2' },
                { color: '#7d8ff4' },
                { color: '#a6b4fd' },
                { color: '#c7d2fe', hasBorder: true },
                { color: '#e3e6fe', hasBorder: true },
                { color: '#eff1fe', hasBorder: true },

                { color: '#4f1b9a' },
                { color: '#5c1fb5' },
                { color: '#6d27da' },
                { color: '#7b3be9' },
                { color: '#8b5cf4' },
                { color: '#a78cfb' },
                { color: '#c6b6fb' },
                { color: '#ddd7ff', hasBorder: true },
                { color: '#eeeaff', hasBorder: true },
                { color: '#f6f4ff', hasBorder: true },

                { color: '#811843' },
                { color: '#9c174b' },
                { color: '#bc195e' },
                { color: '#db2677' },
                { color: '#ec489b' },
                { color: '#f574b6' },
                { color: '#f8a8d3' },
                { color: '#fccfe8', hasBorder: true },
                { color: '#fde7f3', hasBorder: true },
                { color: '#fcf2f8', hasBorder: true },
            ],
            columns: 10,
        };

        (window.editorSettings.fontColors || []).forEach(definition => {
            config.colors.push({ color: definition });
        });

        return config;
    }
}

window['initEditor'] = initEditor;

// translations
window.addEventListener('load', () => {
    defineTranslations('sk', {
        'Embed iframe': 'Vloženie prvku iframe',
        'In line': 'V súlade',
        'Insert paragraph after block': 'Vloženie odseku podľa bloku',
        'Insert paragraph before block': 'Vloženie odseku pred blok',
        'Link image': 'Odkaz na obrázok',
        'Open in a new tab + NOFOLLOW': 'Otvoriť v novej karte + NOFOLLOW',
        'Original': 'Originál',
        'Resize image': 'Zmena veľkosti obrázka',
        'Toggle caption off': 'Vypnutie titulku',
        'Toggle caption on': 'Zapnutie titulku',
        'URL address': 'Adresa URL',
        'Insert template': 'Vložiť šablonu',
    });

    defineTranslations('cs', {
        'Embed iframe': 'Vložit prvek iframe',
        'In line': 'V řadě',
        'Insert paragraph after block': 'Vložit odstavec po bloku',
        'Insert paragraph before block': 'Vložit odstavec před blok',
        'Link image': 'Odkaz obrázku',
        'Open in a new tab + NOFOLLOW': 'Otevřít v nové kartě + NOFOLLOW',
        'Original': 'Originál',
        'Resize image': 'Změnit velikost obrázku',
        'Toggle caption off': 'Vypnout popisek',
        'Toggle caption on': 'Zapnout popisek',
        'URL address': 'URL adresa',
        'Insert template': 'Vložit šablonu',
        'Title': 'Titulek',
    });
});

function defineTranslations(locale, translations) {
    window.CKEDITOR_TRANSLATIONS = window.CKEDITOR_TRANSLATIONS || {};
    window.CKEDITOR_TRANSLATIONS[locale] = window.CKEDITOR_TRANSLATIONS[locale] || {};
    window.CKEDITOR_TRANSLATIONS[locale].dictionary = window.CKEDITOR_TRANSLATIONS[locale].dictionary || {};

    Object.assign(window.CKEDITOR_TRANSLATIONS[locale].dictionary, translations);
}

function translate(message) {
    let locale = document.documentElement.lang || 'cs';
    defineTranslations(locale, {});

    return window.CKEDITOR_TRANSLATIONS[locale].dictionary[message] || message;
}